import { createPublicClient, erc20Abi, erc20Abi_bytes32, fallback, type Hex, http, type PublicClient, zeroAddress } from "viem"
import * as viemChains from "viem/chains"
import _ from "lodash"
import limit from "promise-limit"
import { type NetworkType, NetworkTypes } from "@provex/eip712-utils/network-types"
import type { SupportedToken, TokenMetadata } from "./types.js"

export type EvmTokenKey = `${number}-${Hex}`

const straightToBytes32Whitelist = new Set<EvmTokenKey>([])

export const createCall = (
  functionName: 'name' | 'symbol' | 'decimals',
  address: Hex,
  useBytes32 = false,
) => ({
  address,
  abi: useBytes32 ? erc20Abi_bytes32 : erc20Abi,
  functionName,
  allowFailure: false,
  args: [] as readonly [],
})

export const createCalls = (address: Hex, useBytes32 = false) => [
  createCall('symbol', address, useBytes32),
  createCall('name', address, useBytes32),
  createCall('decimals', address, useBytes32),
] as const

const metdataCallCount = createCalls(zeroAddress, false).length

const metadataCache = new Map<string, TokenMetadata>()

/**
 * Read ERC20 metadata for a single address using direct calls
 * @param client PublicClient to use for the direct calls
 * @param address Address to read metadata for
 * @param useBytes32 Whether to use bytes32 ABI
 * @returns TokenMetadata or null if failed
 */
const readErc20Direct = async (
  client: PublicClient,
  address: Hex,
  useBytes32 = false,
): Promise<TokenMetadata | null> => {
  try {
    const abi = useBytes32 ? erc20Abi_bytes32 : erc20Abi
    const [symbol, name, decimals] = await Promise.all([
      client.readContract({ address, abi, functionName: 'symbol' }),
      client.readContract({ address, abi, functionName: 'name' }),
      client.readContract({ address, abi, functionName: 'decimals' }),
    ])
    return {
      symbol: symbol as string,
      name: name as string,
      decimals: decimals as number,
    }
  } catch {
    return null
  }
}

/**
 * Read ERC20 metadata for a list of addresses
 * @param client PublicClient to use for the multicall
 * @param addresses List of addresses to read metadata for
 * @returns Map of address to TokenMetadata
 */
export const erc20Read = async (client: PublicClient, addresses: string[]): Promise<Map<string, TokenMetadata>> => {
  const toKey = (address: string) => `${client.chain!.id}-${address.toLowerCase() as Hex}` as const
  const createWhitelistedCalls = (address: string) => {
    const whitelisted = straightToBytes32Whitelist.has(toKey(address))
    return createCalls(address as Hex, whitelisted)
  }
  let nullCount = addresses.length
  const cache = new Map<string, TokenMetadata>()
  for (const address of addresses) {
    const existing = metadataCache.get(toKey(address))
    if (existing) {
      nullCount--
      cache.set(address.toLowerCase(), { ...existing })
    }
  }
  if (!nullCount) return cache

  const hasMulticall3 = !!client.chain?.contracts?.multicall3

  // If multicall3 is not available, use direct calls with concurrency limit
  if (!hasMulticall3) {
    console.warn('Chain %o does not have multicall3, falling back to direct calls', client.chain?.name || client.chain?.id)
    const remainingAddresses = addresses.filter(addr => !cache.has(addr.toLowerCase()))

    // Process with concurrency limit of 100 using promise-limit
    const limiter = limit<string>(100)

    await limiter.map(remainingAddresses, async (address) => {
      const whitelisted = straightToBytes32Whitelist.has(toKey(address))
      let metadata = await readErc20Direct(client, address as Hex, whitelisted)

      // Fallback to bytes32 if standard ABI failed
      if (!metadata && !whitelisted) {
        metadata = await readErc20Direct(client, address as Hex, true)
        if (metadata) {
          console.warn('Add to bytes32 whitelist chainId=%o token=%o', client.chain?.id, address)
          straightToBytes32Whitelist.add(toKey(address))
        }
      }
      if (metadata) {
        cache.set(address.toLowerCase(), metadata)
        metadataCache.set(toKey(address), metadata)
      }
    })
    return cache
  }

  // Use multicall3 for batch reading
  // Try with the preferred ABI first
  try {
    const results = await client.multicall({
      multicallAddress: client.chain?.contracts?.multicall3?.address as Hex,
      contracts: [
        ...addresses.flatMap(createWhitelistedCalls),
      ],
    })
    _.chunk(results, metdataCallCount).forEach((chunk, i) => {
      const address = addresses[i]!
      const [symbolResult, nameResult, decimalsResult] = chunk
      if (nameResult?.status === 'success' && symbolResult?.status === 'success' && decimalsResult?.status === 'success') {
        const metadata = {
          symbol: symbolResult.result as string,
          name: nameResult.result as string,
          decimals: decimalsResult.result as number,
        }
        nullCount--
        cache.set(address.toLowerCase(), metadata)
        metadataCache.set(toKey(address), metadata)
        // } else {
        //   console.log(chunk)
      }
    })
  } catch (error) {
    console.error('Multicall failed for chain %o: %o', client.chain?.name || client.chain?.id, error)
    // Continue to fallback handling
  }

  if (!nullCount) return cache
  const missingResults = addresses.filter(addr => !cache.has(addr.toLowerCase()))

  if (!missingResults.length) return cache

  // Fallback to bytes32 ABI for non-whitelisted tokens
  try {
    const resultsBytes32 = await client.multicall({
      contracts: [
        ...missingResults.flatMap((address) => createCalls(address as Hex, true)),
      ],
    })
    _.chunk(resultsBytes32, metdataCallCount).forEach((chunk, i) => {
      const address = missingResults[i]!
      const [symbolResult, nameResult, decimalsResult] = chunk
      if (nameResult?.status === 'success' && symbolResult?.status === 'success' && decimalsResult?.status === 'success') {
        const chainId = client.chain!.id
        console.warn('Add to bytes32 whitelist chainId=%o token=%o', chainId, address)
        const key = toKey(address)
        straightToBytes32Whitelist.add(key)
        const metadata = {
          symbol: symbolResult.result as string,
          name: nameResult.result as string,
          decimals: decimalsResult.result as number,
        }
        cache.set(address.toLowerCase(), metadata)
        metadataCache.set(toKey(address), metadata)
      } else {
        console.warn('Failed to get evm token metadata for chain=%o address=%o', client.chain?.id, address)
      }
    })
  } catch (error) {
    console.error('Bytes32 multicall fallback failed for chain %o: %o', client.chain?.name || client.chain?.id, error)
    // Log but don't throw - return whatever we managed to fetch
  }

  return cache
}

const entries = Object.entries(viemChains) as [string, viemChains.Chain][]
const byId = new Map(entries.map(([, chain]) => [chain.id, chain]))

// Add custom chain: Hyperliquid
byId.set(999, {
  id: 999,
  name: 'Hyperliquid',
  rpcUrls: {
    default: {
      http: ['https://rpc.hyperliquid.xyz/evm'],
    },
  },
  blockTime: 70,
  blockExplorers: {
    default: {
      name: 'Hyperliquid',
      url: 'https://app.hyperliquid.xyz/explorer',
    },
  },
  contracts: {
    multicall3: {
      blockCreated: 13051,
      address: '0xca11bde05977b3631167028862be2a173976ca11',
    },
  },
  nativeCurrency: {
    name: 'HYPE',
    symbol: 'HYPE',
    decimals: 18,
  },
} as viemChains.Chain)

// Add custom chain: Plasma
// Note: RPC URLs must be configured via environment variable RPC_URL_EVM_9745
byId.set(9745, {
  id: 9745,
  name: 'Plasma',
  rpcUrls: {
    default: {
      http: [], // Must be configured via environment variable
    },
  },
  blockTime: 2,
  blockExplorers: {
    default: {
      name: 'Plasma Explorer',
      url: 'https://explorer.plasma.com', // Update with actual explorer URL
    },
  },
  nativeCurrency: {
    name: 'Plasma',
    symbol: 'PLASMA',
    decimals: 18,
  },
} as viemChains.Chain)

// Add custom chain: MegaETH
// Note: RPC URLs must be configured via environment variable RPC_URL_EVM_4326
byId.set(4326, {
  id: 4326,
  name: 'MegaETH',
  rpcUrls: {
    default: {
      http: [], // Must be configured via environment variable
    },
  },
  blockTime: 2,
  blockExplorers: {
    default: {
      name: 'MegaETH Explorer',
      url: 'https://explorer.megaeth.org', // Update with actual explorer URL
    },
  },
  nativeCurrency: {
    name: 'MegaETH',
    symbol: 'MEGAETH',
    decimals: 18,
  },
} as viemChains.Chain)

export const getChainById = _.memoize((chainId: number): viemChains.Chain | null => {
  return byId.get(chainId) ?? null
})

export const mustGetChainById = (chainId: number): viemChains.Chain => {
  const chain = getChainById(chainId)
  if (!chain) {
    // Provide a helpful error message with guidance
    const errorMessage = `Chain ${chainId} not found in viem/chains. ` +
      `Please add it to the custom chains registry in packages/sacrifice-utils/src/index.ts`
    console.error(errorMessage)
    throw new Error(errorMessage)
  }
  return chain
}

// // Singleton client cache with batching enabled
// const clientCache = new Map<string, PublicClient>()

/**
 * Get or create a singleton PublicClient for a given chain and RPC URL
 * Enables true request batching by reusing the same client instance
 * @param chain - The viem chain configuration
 * @param rpcUrl - Optional RPC URL for the chain (uses chain default if not provided)
 * @returns Singleton PublicClient instance
 */
export const getEvmClient = _.memoize(
  (chain: viemChains.Chain, rpcUrls: string[]): PublicClient => {
    return createPublicClient({
      chain,
      transport: fallback(
        rpcUrls.map(url =>
          http(url, {
            batch: true,
            timeout: 10_000, // 10 second timeout
          })
        ),
        {
          retryCount: 3,
          retryDelay: 1000,
        }
      ),
    })
  },
  (chain: viemChains.Chain, rpcUrl: string[] = []) => `${chain.id}-${rpcUrl.join(',')}`
)

/**
 * Get or create a singleton PublicClient by chain ID
 * Uses predefined chain configurations
 * Prefers reliable RPC endpoints when available
 * @param chainId - The chain ID
 * @param rpcUrl - Optional RPC URL for the chain (uses reliable mapping if not provided)
 * @returns Singleton PublicClient instance
 */
export const getEvmClientByChainId = (chainId: number, rpcUrls: string[] = []): PublicClient => {
  const chain = mustGetChainById(chainId);
  // Use reliable RPC mapping if no URL provided, otherwise use provided URL
  const finalRpcUrls = rpcUrls.length ? rpcUrls : (reliableRpcMapping[chainId] || chain.rpcUrls.default.http) as string[];
  return getEvmClient(chain, finalRpcUrls)
}

const reliableRpcMapping: Record<number, string[]> = {
  1: ['https://ethereum-rpc.publicnode.com', 'https://eth.drpc.org', 'https://rpc-ethereum.g4mm4.io'], // Ethereum
  56: [
    'https://bsc-rpc.publicnode.com',
    'https://endpoints.omniatech.io/v1/bsc/mainnet/public',
    'https://binance.llamarpc.com',
    'https://bsc.blockrazor.xyz',
  ], // BSC
  137: ['https://polygon-bor-rpc.publicnode.com'], // Polygon
  42161: ['https://arbitrum-one-rpc.publicnode.com'], // Arbitrum
  10: ['https://optimism-rpc.publicnode.com'], // Optimism
  43114: ['https://avalanche-c-chain-rpc.publicnode.com'], // Avalanche
  8453: ['https://base-rpc.publicnode.com'], // Base
  369: ['https://rpc-pulsechain.g4mm4.io', 'https://rpc.pulsechain.com'], // Pulsechain
  146: ['https://rpc.soniclabs.com', 'https://sonic-json-rpc.stakely.io', 'https://sonic.drpc.org'], // Sonic
  81457: ['https://blast-rpc.publicnode.com'], // Blast
  999: ['https://rpc.hypurrscan.io'], // Hyperliquid
  9745: ['https://rpc.plasma.to'], // Plasma
  480: ['https://worldchain-mainnet.gateway.tenderly.co'], // Worldchain
  1284: ['https://moonbeam-rpc.publicnode.com'], // Moonbeam
  1285: ['https://moonriver-rpc.publicnode.com'], // Moonriver
}

/**
 * Get the reliable RPC URL for a chain ID
 * @param chainId - The chain ID
 * @returns Reliable RPC URL if available, otherwise undefined
 */
export const getReliableRpcUrl = (chainId: number): string[] | null => {
  return reliableRpcMapping[chainId] ?? null
}

/**
 * Get the reliable RPC URL for a chain ID, with fallback to viem chain definitions
 * @param chainId - The chain ID
 * @returns RPC URLs from reliable mapping or viem chain definitions
 * @throws Error if no RPC URLs are available
 */
export const mustGetReliableRpcUrl = (chainId: number): string[] => {
  // Try reliable mapping first
  const rpcUrls = getReliableRpcUrl(chainId)
  if (rpcUrls && rpcUrls.length > 0) {
    return rpcUrls
  }

  // Fallback to viem chain definitions
  const chain = getChainById(chainId)
  if (chain && chain.rpcUrls.default.http.length > 0) {
    return chain.rpcUrls.default.http as string[]
  }

  const chainName = chain?.name || 'Unknown'
  throw new Error(
    `No RPC URL found for chain ${chainId} (${chainName}). ` +
    `Please configure environment variable RPC_URL_EVM_${chainId} with a comma-separated list of RPC URLs.`
  )
}

/**
 * Get the block explorer URL for a chain ID
 * @param chainId - The chain ID
 * @returns Block explorer URL if available, otherwise null
 */
export const getEvmExplorerUrl = (chainId: number): string | null => {
  return getChainById(chainId)?.blockExplorers?.default?.url ?? null
}

// Mapping of BIP32 chain IDs to Blockchair coin identifiers
const BIP32_CHAIN_TO_BLOCKCHAIR: Record<number, string> = {
  0: 'bitcoin',
  2: 'litecoin',
  3: 'dogecoin',
  145: 'bitcoin-cash',
  5: 'dash',
  133: 'zcash',
}

/**
 * Get explorer transaction URL for any network type
 * @param networkType - The network type (evm, bip32, memo, etc.)
 * @param chainId - The chain ID
 * @param txHash - The transaction hash
 * @returns Explorer URL for the transaction
 */
export const getExplorerTxUrl = (networkType: NetworkType, chainId: number, txHash: string): string => {
  // Handle BIP32 networks (Bitcoin-like chains)
  if (networkType === NetworkTypes.BIP32) {
    const coinName = BIP32_CHAIN_TO_BLOCKCHAIR[chainId]
    if (!coinName) {
      return `Unknown BIP32 chain: ${chainId}`
    }
    return `https://blockchair.com/${coinName}/transaction/${txHash}`
  }

  // Handle Monero
  if (networkType === NetworkTypes.MONERO) {
    return `https://blockchair.com/monero/transaction/${txHash}`
  }

  // Handle TVM (TON/Tron)
  if (networkType === NetworkTypes.TVM) {
    return `https://tronscan.org/#/transaction/${txHash}`
  }

  // Handle Memo networks (XRP and Stellar)
  if (networkType === NetworkTypes.MEMO) {
    if (chainId === 144) {
      return `https://xrpscan.com/tx/${txHash}`
    }
    if (chainId === 148) {
      return `https://stellar.expert/explorer/public/tx/${txHash}`
    }
    return `Unknown memo chain: ${chainId}`
  }

  // Handle Solana
  if (networkType === NetworkTypes.SOLANA) {
    return `https://explorer.solana.com/tx/${txHash}`
  }

  // Handle Cardano
  if (networkType === NetworkTypes.CARDANO) {
    return `https://cardanoscan.io/transaction/${txHash}`
  }

  // Handle EVM networks
  if (networkType === NetworkTypes.EVM) {
    const baseUrl = getEvmExplorerUrl(chainId)
    if (!baseUrl) {
      return `Unknown EVM chain: ${chainId}`
    }
    return `${baseUrl}/tx/${txHash}`
  }

  return `Unknown network: ${networkType}`
}


export const mapToTokenMetadata = {
  [NetworkTypes.BIP32]: {
    0: { symbol: 'BTC', name: 'Bitcoin', decimals: 8 },
    2: { symbol: 'LTC', name: 'Litecoin', decimals: 8 },
    3: { symbol: 'DOGE', name: 'Dogecoin', decimals: 8 },
    145: { symbol: 'BCH', name: 'Bitcoin Cash', decimals: 8 },
    5: { symbol: 'DASH', name: 'Dash', decimals: 8 },
    133: { symbol: 'ZEC', name: 'Zcash', decimals: 8 },
  },
  [NetworkTypes.MEMO]: {
    144: { symbol: 'XRP', name: 'Ripple', decimals: 6 },
    148: { symbol: 'XLM', name: 'Stellar', decimals: 7 },
  },
  [NetworkTypes.SOLANA]: {
    501: { symbol: 'SOL', name: 'Solana', decimals: 9 },
  },
  [NetworkTypes.MONERO]: {
    128: { symbol: 'XMR', name: 'Monero', decimals: 12 },
  },
  [NetworkTypes.TVM]: {
    1000: { symbol: 'TRX', name: 'Tron', decimals: 6 },
  },
  [NetworkTypes.CARDANO]: {
    1852: { symbol: 'ADA', name: 'Cardano', decimals: 6 },
  },
  [NetworkTypes.EVM]: {},
} as Record<NetworkType, Record<number, TokenMetadata>>

export const getTokenMetadata = async (input: SupportedToken): Promise<TokenMetadata | null> => {
  const { networkType, chainId, tokenAddress } = input
  if (networkType === NetworkTypes.EVM) {
    if (tokenAddress === zeroAddress) {
      const chain = mustGetChainById(chainId)
      return {
        symbol: chain.nativeCurrency.symbol,
        name: chain.nativeCurrency.name,
        decimals: chain.nativeCurrency.decimals,
      }
    }

    try {
      const client = getEvmClientByChainId(chainId)
      const tokensMetadata = await erc20Read(client, [tokenAddress])
      // console.log(chainId, tokenAddress, tokensMetadata)
      return tokensMetadata.get(tokenAddress.toLowerCase()) ?? null
    } catch (error) {
      console.error('Failed to get token metadata for chain=%o address=%o: %o', chainId, tokenAddress, error)
      return null
    }
  }
  const group = mapToTokenMetadata[networkType]
  return group?.[chainId] ?? null
}

export const hasUniversalDepositAddress = (type: NetworkType) => {
  return type === NetworkTypes.EVM || type === NetworkTypes.TVM
}

// Export decay calculation utilities
export * from './decay-calculation.js'
// Export types
export * from './types.js'
// Export points
export * from './points.js'
// Export address utilities
export * from './address.js'

export const MAX_SIGNATURE_AGE_MS = 60 * 60 * 1000 // 1 hour
